/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useState } from 'react';
import { RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import styled from 'styled-components/native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { TextTransform } from 'react-native-localized-text';
import { useSelector } from 'react-redux';

import PreviewHeader from 'components/molecules/preview-header';
import { TextInputWithLabel } from 'components/molecules/input-with-label';
import DropDownWithLabel from 'components/molecules/dropdown-with-label';
import ButtonWithIcon from 'components/atoms/button-with-icon';

import { LeadAction } from 'domain/shared/types/lead-action';
import { RootState } from 'domain/shared/store';
import useAuth from 'domain/shared/hooks/useAuth';
import ActionService from 'domain/shared/services/action-service';

import useSubmitForm from 'model/hooks/use-submit-form';
import colors from 'theme/colors';
import { translate } from 'locales';
import { Header2 } from 'components/molecules/text';

const validationSchema = yup.object({
  customer_name: yup.string().required(),
  customer_phone: yup.string().required(),
  customer_email: yup.string().required(),
  have_tradein: yup.string().required(),
  tradein_year: yup.string(),
  tradein_make: yup.string(),
  tradein_model: yup.string(),
  tradein_miles: yup.string(),
  current_payment: yup.string(),
  like_current_vehicle: yup.string(),
  not_like_current_vehicle: yup.string(),
  vehicle_stock: yup.string(),
  vehicle_must_have: yup.string(),
  payment_goal: yup.string(),
  rebates_discounts: yup.string(),
  have_financing: yup.string(),
  credit_amount: yup.string(),
  down_payment: yup.string(),
  earn_business: yup.string(),
  interested_financing: yup.string(),
});

type Form = yup.InferType<typeof validationSchema>;

interface Props {
  route: RouteProp<ParamList, 'Any'>;
  navigation: NativeStackNavigationProp<ParamList, 'Any'>;
}

export default function NeedAnalysisForm(props: Props) {
  const { navigation, route } = props;
  const { action, onGoBack } = route.params;

  const [loading, setLoading] = useState(false);

  const { user } = useAuth();
  const lead = useSelector((state: RootState) => state.opportunity.activeOpportunity)!;

  const { submit } = useSubmitForm(action);

  function handleSubmitted(value: boolean) {
    if (!value) return;
    onGoBack?.();
    navigation.goBack();
  }

  const formik = useFormik<Form>({
    initialValues: {
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      have_tradein: '',
      tradein_year: '',
      tradein_make: '',
      tradein_model: '',
      tradein_miles: '',
      current_payment: '',
      like_current_vehicle: '',
      not_like_current_vehicle: '',
      vehicle_stock: '',
      vehicle_must_have: '',
      payment_goal: '',
      rebates_discounts: '',
      have_financing: '',
      credit_amount: '',
      down_payment: '',
      earn_business: '',
      interested_financing: '',
    },
    validationSchema,
    onSubmit(value) {
      setLoading(true);

      const request = [
        { label: 'Customer Name', value: value.customer_name },
        { label: 'Customer Phone', value: value.customer_phone },
        { label: 'Customer Email', value: value.customer_email },
        { label: 'Do you have a trade-in?', value: value.have_tradein },
        { label: 'Trade-in Year', value: value.tradein_year },
        { label: 'Trade-in Make', value: value.tradein_make },
        { label: 'Trade-in Model', value: value.tradein_model },
        { label: 'Trade-in Miles', value: value.tradein_miles },
        { label: 'What is your current payment?', value: value.current_payment },
        { label: 'What do you like about your current vehicle?', value: value.like_current_vehicle },
        { label: 'What are you looking forward to the most about the new vehicle?', value: value.not_like_current_vehicle },
        { label: 'Vehicle of Interest Stock #', value: value.vehicle_stock },
        { label: 'Must have options on your next vehicle', value: value.vehicle_must_have },
        { label: 'What is your payment goal for the new vehicle?', value: value.payment_goal },
        { label: 'Rebates and Discounts (Select all that apply)', value: value.rebates_discounts },
        { label: 'Will you be taking advantage of our world class financing?', value: value.have_financing },
        { label: 'On a scale of 1-10 with 1- being the best, how is your credit?', value: value.credit_amount },
        { label: 'How much have you prepared for a down payment?', value: value.down_payment },
        { label: 'Any reason we couldn’t earn your business now?', value: value.earn_business },
        { label: 'Are you interested in ways to save additional money and/or obtain a lower payment?', value: value.interested_financing },
      ];

      submit(request)
        .then(handleSubmitted)
        .finally(() => setLoading(false));
    },
  });

  const {
    values, errors, touched, handleChange, handleBlur,
  } = formik;

  const getLinkedFields = async () => {
    if (!lead || !user) return;

    const { uuid: lead_id } = lead;
    const { default_company_uuid: company_id } = user;

    setLoading(true);
    const response = await ActionService.getLinkedFields(lead_id, company_id);
    if (!response) return;


    formik.setValues({
      ...values,
      customer_name: response?.['contact.print-name'] || '',
      customer_phone: response?.['contact.phone'] || '',
      customer_email: response?.['contact.email'] || '',
      tradein_year: response?.['lead.trade-in.year'] || '',
      tradein_model: response?.['lead.trade-in.model'] || '',
      tradein_make: response?.['lead.trade-in.make'] || '',
      tradein_miles: response?.['lead.trade-in.odometer'] || '',
    });

    setLoading(false);
  };

  const TradeinComponent = values.have_tradein.toLowerCase() === 'yes' && (
    <>
      <TextInputWithLabel
        label="Trade-in Year"
        handleChange={handleChange('tradein_year')}
        onBlur={handleBlur('tradein_year')}
        value={values.tradein_year}
        type="input"
        avoidTranslation
        keyboardType="numeric"
      />

      <TextInputWithLabel
        label="Trade-in Make"
        handleChange={handleChange('tradein_make')}
        onBlur={handleBlur('tradein_make')}
        value={values.tradein_make}
        type="input"
        avoidTranslation
      />

      <TextInputWithLabel
        label="Trade-in Model"
        handleChange={handleChange('tradein_model')}
        onBlur={handleBlur('tradein_model')}
        value={values.tradein_model}
        type="input"
        avoidTranslation
      />

      <TextInputWithLabel
        label="Trade-in Miles"
        handleChange={handleChange('tradein_miles')}
        onBlur={handleBlur('tradein_miles')}
        value={values.tradein_miles}
        type="input"
        avoidTranslation
        keyboardType="numeric"
      />
    </>
  );

  useEffect(() => {
    getLinkedFields();
  }, [lead, user]);

  return (
    <Container>
      <PreviewHeader
        actionName={action.description}
        onBack={() => navigation.goBack()}
      />

      <Content>
        <Header2 text="Guest Info" />

        <TextInputWithLabel
          label="Customer Name"
          handleChange={handleChange('customer_name')}
          onBlur={handleBlur('customer_name')}
          value={values.customer_name}
          error={touched.customer_name ? errors.customer_name : undefined}
          type="input"
          required
          showErrorMsg={false}
          avoidTranslation
        />

        <TextInputWithLabel
          label="Customer Phone"
          handleChange={handleChange('customer_phone')}
          onBlur={handleBlur('customer_phone')}
          value={values.customer_phone}
          error={touched.customer_phone ? errors.customer_phone : undefined}
          type="input"
          required
          showErrorMsg={false}
          avoidTranslation
        />

        <TextInputWithLabel
          label="Customer Email"
          handleChange={handleChange('customer_email')}
          onBlur={handleBlur('customer_email')}
          value={values.customer_email}
          error={touched.customer_email ? errors.customer_email : undefined}
          type="input"
          required
          showErrorMsg={false}
          avoidTranslation
        />

        <Header2 text="Current Vehicle" />

        <DropDownWithLabel
          label="Do you have a trade-in?"
          onItemSelected={(value) => formik.setFieldValue('have_tradein', value.label)}
          errors={touched.have_tradein ? errors.have_tradein : ''}
          items={[
            {
              value: 'yes',
              label: 'Yes',
            },
            {
              value: 'no',
              label: 'No',
            },
          ]}
        />

        {TradeinComponent}

        <TextInputWithLabel
          label="What is your current payment?"
          handleChange={handleChange('current_payment')}
          onBlur={handleBlur('current_payment')}
          value={values.current_payment}
          type="currency"
          avoidTranslation
        />

        <TextInputWithLabel
          label="What do you like about your current vehicle?"
          handleChange={handleChange('like_current_vehicle')}
          onBlur={handleBlur('like_current_vehicle')}
          value={values.like_current_vehicle}
          type="input"
          multiline
          avoidTranslation
        />

        <TextInputWithLabel
          label="What are you looking forward to the most about the new vehicle?"
          handleChange={handleChange('not_like_current_vehicle')}
          onBlur={handleBlur('not_like_current_vehicle')}
          value={values.not_like_current_vehicle}
          type="input"
          multiline
          avoidTranslation
        />

        <Header2 text="New Vehicle" />

        <TextInputWithLabel
          label="Vehicle of Interest Stock #"
          handleChange={handleChange('vehicle_stock')}
          onBlur={handleBlur('vehicle_stock')}
          value={values.vehicle_stock}
          type="input"
          avoidTranslation
        />

        <TextInputWithLabel
          label="Must have options on your next vehicle"
          handleChange={handleChange('vehicle_must_have')}
          onBlur={handleBlur('vehicle_must_have')}
          value={values.vehicle_must_have}
          type="input"
          avoidTranslation
        />

        <TextInputWithLabel
          label="What is your payment goal for the new vehicle?"
          handleChange={handleChange('payment_goal')}
          onBlur={handleBlur('payment_goal')}
          value={values.payment_goal}
          type="currency"
          avoidTranslation
        />

        <DropDownWithLabel
          multiple
          label="Rebates and Discounts (Select all that apply)"
          onItemSelected={(value) => {
            formik.setFieldValue('rebates_discounts', value.map(l => l.label).join(', '));
          }}
          errors={touched.rebates_discounts ? errors.rebates_discounts : ''}
          items={[
            {
              value: 'active_military',
              label: 'Active Military/First Responder',
            },
            {
              value: 'work_ford',
              label: 'You or a family member work for Ford',
            },
            {
              value: 'special_offers',
              label: 'Received special offers from Ford',
            },
          ]}
        />

        <DropDownWithLabel
          label="Will you be taking advantage of our world class financing?"
          onItemSelected={(value) => formik.setFieldValue('have_financing', value.label)}
          errors={touched.have_financing ? errors.have_financing : ''}
          items={[
            {
              value: 'yes',
              label: 'Yes',
            },
            {
              value: 'no',
              label: 'No',
            },
          ]}
        />

        <TextInputWithLabel
          label="On a scale of 1-10 with 1- being the best, how is your credit?"
          handleChange={handleChange('credit_amount')}
          onBlur={handleBlur('credit_amount')}
          value={values.credit_amount}
          type="input"
          avoidTranslation
        />

        <TextInputWithLabel
          label="How much have you prepared for a down payment?"
          handleChange={handleChange('down_payment')}
          onBlur={handleBlur('down_payment')}
          value={values.down_payment}
          type="currency"
          avoidTranslation
        />

        <TextInputWithLabel
          label="Any reason we couldn’t earn your business now?"
          handleChange={handleChange('earn_business')}
          onBlur={handleBlur('earn_business')}
          value={values.earn_business}
          type="input"
          avoidTranslation
        />

        <DropDownWithLabel
          label="Are you interested in ways to save additional money and/or obtain a lower payment?"
          onItemSelected={(value) => formik.setFieldValue('interested_financing', value.label)}
          errors={touched.interested_financing ? errors.interested_financing : ''}
          items={[
            {
              value: 'yes',
              label: 'Yes',
            },
            {
              value: 'no',
              label: 'No',
            },
          ]}
        />

        <ButtonWithIcon
          loading={loading}
          text={translate('submit', TextTransform.CAPITAL)}
          onPress={formik.submitForm}
        />
      </Content>
    </Container>
  );
}

const Container = styled.View`
  flex: 1px;
  background-color: ${colors.WHITE};
`;

const Content = styled(KeyboardAwareScrollView).attrs({
  showsVerticalScrollIndicator: false,
  extraScrollHeight: 100,
  contentContainerStyle: { gap: 20, paddingBottom: 100 },
})`
  padding: 20px;
  padding-bottom: 100px;
`;

type ParamList = {
  Any: { action: LeadAction, onGoBack: VoidFunction }
};
